import { Fragment, useMemo, useState } from "react"
import { Link } from "@tanstack/react-router"
import { IconMagnifierOutline18 as SearchIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Input, Loader, Tabs, TabsList, TabsTrigger } from "@productlane/ui"

import type { SettingsAdoptionFilter } from "./settings-adoption-view"
import { api } from "@/lib/api"
import {
	ALL_SETTINGS_NAV_ITEMS,
	ALL_SETTINGS_SUBPAGE_NAV_ITEMS,
	SETTINGS_SUBPAGE_PARENT_BY_HREF,
} from "@/screens/settings/sidebar"
import { SETTING_LOCATIONS } from "./settings-adoption-layout"
import {
	buildSettingsPages,
	groupSettingsByLayout,
	settingShare,
} from "./settings-adoption-view"

const FILTERS: Array<{ value: SettingsAdoptionFilter; label: string }> = [
	{ value: "all", label: "All" },
	{ value: "changed", label: "Changed" },
	{ value: "untouched", label: "Untouched" },
]

const SETTINGS_PAGES = buildSettingsPages(
	ALL_SETTINGS_NAV_ITEMS,
	ALL_SETTINGS_SUBPAGE_NAV_ITEMS,
	SETTINGS_SUBPAGE_PARENT_BY_HREF,
)

export function SettingsAdoptionTable() {
	const [filter, setFilter] = useState<SettingsAdoptionFilter>("all")
	const [search, setSearch] = useState("")
	const query = api.analytics.settingsAdoption.useQuery(
		{},
		{ staleTime: 30000 },
	)

	const groups = useMemo(
		() =>
			groupSettingsByLayout(
				query.data?.settings ?? [],
				SETTING_LOCATIONS,
				SETTINGS_PAGES,
				filter,
				search,
			),
		[query.data, filter, search],
	)

	if (!query.data || query.isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader />
			</div>
		)
	}

	return (
		<div className="rounded-lg border border-gray-200 dark:border-gray-700/80">
			<div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3 dark:border-gray-700/80">
				<h3 className="text-sm font-medium">Settings changed from default</h3>
				<div className="flex flex-none items-center gap-4">
					<span className="text-sm text-gray-500 dark:text-gray-400">
						{query.data.totalPaying} paying customers
					</span>
					<Input
						name="settings-adoption-search"
						variant="borderless"
						textSize="sm"
						icon={SearchIcon}
						placeholder="Find setting..."
						value={search}
						className="w-44 px-0 placeholder:text-tertiary"
						inputClassName="h-7"
						onChange={(event) => setSearch(event.target.value)}
					/>
					<Tabs
						value={filter}
						onValueChange={(value) =>
							setFilter(value as SettingsAdoptionFilter)
						}
					>
						<TabsList variant="transparent">
							{FILTERS.map((option) => (
								<TabsTrigger
									key={option.value}
									variant="transparent"
									value={option.value}
								>
									{option.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700/80 dark:text-gray-400">
						<th className="px-4 py-2 font-medium">Setting</th>
						<th className="px-4 py-2 font-medium">Default</th>
						<th className="px-4 py-2 text-right font-medium">Changed</th>
						<th className="w-48 px-4 py-2 text-right font-medium">Share</th>
					</tr>
				</thead>
				<tbody>
					{groups.length === 0 ? (
						<tr>
							<td
								colSpan={4}
								className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
							>
								No setting matches this filter
							</td>
						</tr>
					) : null}
					{groups.map((group) => (
						<Fragment key={group.title}>
							<tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700/80 dark:bg-gray-800/50">
								<td colSpan={4} className="px-4 py-2 text-xs font-medium">
									{group.href ? (
										<Link
											to={group.href}
											className="text-gray-700 hover:underline dark:text-gray-200"
										>
											{group.title}
										</Link>
									) : (
										<span className="text-gray-500 dark:text-gray-400">
											{group.title}
										</span>
									)}
								</td>
							</tr>
							{group.sections.map((section, sectionIndex) => (
								<Fragment
									key={`${group.title}-${section.title ?? sectionIndex}`}
								>
									{section.title ? (
										<tr>
											<td
												colSpan={4}
												className="px-4 pb-1 pt-3 text-xs text-gray-500 dark:text-gray-400"
											>
												{section.title}
											</td>
										</tr>
									) : null}
									{section.rows.map((row) => {
										const share = settingShare(row)
										return (
											<tr
												key={row.key}
												title={row.key}
												className={cn(
													"border-b border-gray-200 last:border-b-0 dark:border-gray-700/80",
													row.changed === 0 &&
														"text-gray-400 dark:text-gray-500",
												)}
											>
												<td className="px-4 py-2 align-top">
													{row.uiLabel}
													{row.values.length > 0 ? (
														<span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
															{row.values.map((value) => (
																<span key={value.value}>
																	{value.value}{" "}
																	<span className="tabular-nums text-gray-700 dark:text-gray-300">
																		{value.count}
																	</span>
																</span>
															))}
														</span>
													) : null}
												</td>
												<td className="px-4 py-2 align-top text-gray-500 dark:text-gray-400">
													{row.defaultLabel}
												</td>
												<td className="px-4 py-2 text-right align-top tabular-nums">
													{row.changed}
													<span className="text-gray-400 dark:text-gray-500">
														{" "}
														/ {row.base}
													</span>
												</td>
												<td className="px-4 py-2 align-top">
													<span className="flex items-center justify-end gap-2">
														<span className="h-1.5 w-24 flex-none overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
															<span
																className="block h-full rounded-full bg-gray-400 dark:bg-gray-500"
																style={{ width: `${share}%` }}
															/>
														</span>
														<span className="w-10 text-right tabular-nums text-gray-500 dark:text-gray-400">
															{share}%
														</span>
													</span>
												</td>
											</tr>
										)
									})}
								</Fragment>
							))}
						</Fragment>
					))}
				</tbody>
			</table>
		</div>
	)
}
